"use client";

import {
  Checkbox,
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { CRONBACHS_ALPHA_CALCULATOR_STRINGS } from "@/components/tools/cronbachs-alpha-calculator";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  apaBounded,
  type Formatter,
  fixed,
} from "@/lib/tool-kit/stats-research/format";
import {
  type AlphaBand,
  type AlphaResult,
  alphaBand,
} from "@/lib/tool-kit/stats-research/reliability";

/*
 * The item table and the result panel of the Cronbach's alpha calculator, split out to
 * keep the widget file readable. Strings are the widget's own, passed in.
 */

type Strings = typeof CRONBACHS_ALPHA_CALCULATOR_STRINGS;

const DASH = "—";

const BAND_KEY: Record<AlphaBand, keyof Strings> = {
  excellent: "bandExcellent",
  good: "bandGood",
  acceptable: "bandAcceptable",
  questionable: "bandQuestionable",
  poor: "bandPoor",
  unacceptable: "bandUnacceptable",
};

function finiteOr(
  value: number | null | undefined,
  show: (v: number) => string
) {
  return value != null && Number.isFinite(value) ? show(value) : DASH;
}

export function ItemTable({
  names,
  include,
  reverse,
  result,
  onInclude,
  onReverse,
  strings,
  fmt,
}: {
  names: string[];
  include: boolean[];
  reverse: boolean[];
  result: AlphaResult | null;
  onInclude: (index: number) => void;
  onReverse: (index: number) => void;
  strings: Strings;
  fmt: Formatter;
}) {
  const positions = include.flatMap((on, j) => (on ? [j] : []));
  const head = [
    strings.columnItem,
    strings.columnUse,
    strings.columnReverse,
    strings.columnMean,
    strings.columnSd,
    strings.columnItemRest,
    strings.columnIfDeleted,
  ];
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            {head.map((cell) => (
              <th className={TH} key={cell} scope="col">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {names.map((name, j) => {
            const position = positions.indexOf(j);
            const stats = position >= 0 ? result?.items[position] : undefined;
            const negative = (stats?.itemRest ?? 0) < 0;
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: item columns are positional
              <tr key={j}>
                <th className={`${TD} font-medium`} scope="row">
                  {name}
                </th>
                <td className={TD}>
                  <Checkbox
                    checked={include[j]}
                    label={
                      <span className="sr-only">
                        {t(strings.useItem, { item: name })}
                      </span>
                    }
                    onChange={() => onInclude(j)}
                  />
                </td>
                <td className={TD}>
                  <Checkbox
                    checked={reverse[j]}
                    label={
                      <span className="sr-only">
                        {t(strings.reverseItem, { item: name })}
                      </span>
                    }
                    onChange={() => onReverse(j)}
                  />
                </td>
                <td className={`${TD} font-mono`}>
                  {finiteOr(stats?.mean, (v) => fixed(fmt, v, 2))}
                </td>
                <td className={`${TD} font-mono`}>
                  {finiteOr(stats?.sd, (v) => fixed(fmt, v, 2))}
                </td>
                <td
                  className={`${TD} font-mono ${negative ? "text-destructive" : ""}`}
                >
                  {finiteOr(stats?.itemRest, (v) => fixed(fmt, v, 3))}
                </td>
                <td className={`${TD} font-mono`}>
                  {finiteOr(stats?.alphaIfDeleted, (v) => fixed(fmt, v, 3))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Working({
  result,
  strings,
  fmt,
}: {
  result: AlphaResult;
  strings: Strings;
  fmt: Formatter;
}) {
  const three = (value: number) => fixed(fmt, value, 3);
  return (
    <div className={`${CARD} space-y-2`}>
      <h3 className={WIDGET_H3}>{strings.howCalculated}</h3>
      <p className="font-mono text-muted-foreground text-sm leading-relaxed">
        {t(strings.workingAlpha, {
          k: fmt(result.k),
          km1: fmt(result.k - 1),
          itemVar: fixed(fmt, result.itemVarianceSum, 4),
          totalVar: fixed(fmt, result.totalVariance, 4),
          alpha: three(result.alpha),
        })}
      </p>
      {Number.isFinite(result.standardized) ? (
        <p className="font-mono text-muted-foreground text-sm leading-relaxed">
          {t(strings.workingStandardized, {
            k: fmt(result.k),
            km1: fmt(result.k - 1),
            r: three(result.meanInterItem),
            value: three(result.standardized),
          })}
        </p>
      ) : null}
      <p className="text-muted-foreground text-sm leading-relaxed">
        {t(strings.workingFeldt, {
          df1: fmt(result.n - 1),
          df2: fmt((result.n - 1) * (result.k - 1)),
        })}
      </p>
    </div>
  );
}

export function AlphaSummary({
  result,
  names,
  strings,
  fmt,
  locale,
}: {
  result: AlphaResult;
  /** Names of the items in the analysis, in order. */
  names: string[];
  strings: Strings;
  fmt: Formatter;
  locale: string;
}) {
  const three = (value: number) => fixed(fmt, value, 3);
  const negative = names.filter(
    (_, position) => (result.items[position]?.itemRest ?? 0) < 0
  );
  const report = t(strings.report, {
    alpha: apaBounded(fmt, result.alpha),
    lower: apaBounded(fmt, result.lower),
    upper: apaBounded(fmt, result.upper),
    items: tPlural(strings.itemCount, locale, result.k, {
      count: fmt(result.k),
    }).trim(),
    n: fmt(result.n),
  });
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.alpha} value={three(result.alpha)} />
        <StatCard
          label={strings.interval}
          value={`[${three(result.lower)}, ${three(result.upper)}]`}
        />
        <StatCard
          label={strings.standardized}
          value={finiteOr(result.standardized, three)}
        />
        <StatCard label={strings.itemCountLabel} value={fmt(result.k)} />
        <StatCard label={strings.respondents} value={fmt(result.n)} />
        <StatCard
          label={strings.meanR}
          value={finiteOr(result.meanInterItem, three)}
        />
      </StatGrid>
      <p className="text-foreground text-sm leading-relaxed">
        {t(strings.interpretation, {
          alpha: three(result.alpha),
          band: strings[BAND_KEY[alphaBand(result.alpha)]],
        })}{" "}
        {strings.caveat}
      </p>
      {negative.length > 0 ? (
        <Notice>
          {tPlural(strings.negativeItems, locale, negative.length, {
            items: negative.join(", "),
          }).trim()}
        </Notice>
      ) : null}
      {result.alpha > 0.95 ? <Notice>{strings.redundant}</Notice> : null}
      <div className="flex flex-wrap items-center gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          text={report}
        />
        <p className="font-mono text-muted-foreground text-sm">{report}</p>
      </div>
      <Working fmt={fmt} result={result} strings={strings} />
    </section>
  );
}
